// public/v3/src/lib/medication-dedupe.js
//
// When the same drug appears at multiple strengths in Elation's active
// medication list (e.g., a dose change where the older entry wasn't
// discontinued), the AI care-plan + billing-note prompts were listing both
// strengths as if the patient was on both — which misrepresents the
// regimen. This helper groups by drug identity (name with strength stripped,
// while preserving route/form qualifiers) and keeps only the most current
// entry. The dedup happens at prompt-build time only; the underlying stored
// medication list is unchanged so other consumers (panel views, time logs)
// still see the raw chart.
//
// Falls back gracefully when start_date is unavailable (patients synced
// before the mapper started preserving start_date will lack it):
//   1. If only one entry has a date → keep that one
//   2. If both have dates → keep the later
//   3. If neither has a start_date but last_modified is present → keep the
//      most recently modified entry
//   4. If neither has usable dates → keep the later-positioned entry in the
//      input array (Elation typically returns medications in some stable
//      order — usually creation order — so this is the final fallback)

// Strip common dose patterns so two entries for the same medication form at
// different strengths normalize to the same key. Preserve route/form terms
// (patch, sublingual tablet, inhaler, nebulization solution, topical, etc.) so
// clinically distinct active therapies do not collapse into one prompt entry.
// Imperfect for edge cases (e.g., "apixaban" vs "Eliquis (apixaban)" wouldn't
// match), but covers the common case where Elation issues both entries from the
// same drug-database master record.
export function drugIdentityKey(name) {
  return String(name || '')
    .toLowerCase()
    // numeric dose patterns: "2.5 mg", "10-12.5 mg", "500 mcg", "100 units",
    // "0.2 mg/hr", "10mg", "5%"
    .replace(/\b\d+(?:\.\d+)?(?:\s*(?:-|\/)\s*\d+(?:\.\d+)?)*\s*(?:mg|mcg|µg|g|kg|ml|l|iu|units?|cc)(?:\s*\/\s*(?:ml|l|hr|hour|day|24hr))?\b/g, '')
    .replace(/\b\d+(?:\.\d+)?\s*%/g, '')
    // strip parens but keep contents (so "Eliquis (apixaban)" → "eliquis apixaban")
    .replace(/[\(\)\[\]]/g, ' ')
    .replace(/[,;/-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Group medications by drug identity key, keeping the entry with the most
// recent start_date / startDate, then most recent lastModified, then later
// array position. Returns the deduped array in the order of first appearance
// of each key (stable).
export function dedupeMedicationsByDrug(meds) {
  if (!Array.isArray(meds)) return [];
  function parseDateValue(value) {
    const raw = String(value || '').trim();
    if (!raw) return null;
    const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[T\s].*)?$/.exec(raw);
    if (ymd) {
      const y = Number(ymd[1]);
      const m = Number(ymd[2]);
      const d = Number(ymd[3]);
      const time = Date.UTC(y, m - 1, d);
      const dt = new Date(time);
      if (dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d) return time;
      return null;
    }
    const parsed = Date.parse(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  function timingForMedication(m) {
    if (!m || typeof m === 'string') return { start: null, modified: null };
    return {
      start: parseDateValue(m.startDate || m.start_date),
      modified: parseDateValue(m.lastModified || m.last_modified),
    };
  }
  function shouldKeepNew(candidate, existing) {
    if (candidate.timing.start != null || existing.timing.start != null) {
      if (candidate.timing.start != null && existing.timing.start == null) return true;
      if (candidate.timing.start == null && existing.timing.start != null) return false;
      if (candidate.timing.start !== existing.timing.start) return candidate.timing.start > existing.timing.start;
    }
    if (candidate.timing.modified != null || existing.timing.modified != null) {
      if (candidate.timing.modified != null && existing.timing.modified == null) return true;
      if (candidate.timing.modified == null && existing.timing.modified != null) return false;
      if (candidate.timing.modified !== existing.timing.modified) return candidate.timing.modified > existing.timing.modified;
    }
    return candidate.index > existing.index;
  }
  const byKey = new Map();
  meds.forEach((m, i) => {
    // String entries — no date metadata, so apply the same "keep the
    // later-positioned entry in the input array" fallback we use for
    // dateless object entries below.
    if (typeof m === 'string') {
      const key = drugIdentityKey(m);
      if (!key) return;
      const existing = byKey.get(key);
      const candidate = { value: m, index: i, timing: timingForMedication(m) };
      if (!existing || shouldKeepNew(candidate, existing)) byKey.set(key, candidate);
      return;
    }
    if (!m) return;
    const name = (m.name || '').trim();
    if (!name) return;
    const key = drugIdentityKey(name);
    if (!key) return;
    const existing = byKey.get(key);
    const candidate = { value: m, index: i, timing: timingForMedication(m) };
    if (!existing || shouldKeepNew(candidate, existing)) byKey.set(key, candidate);
  });
  return Array.from(byKey.values()).map(entry => entry.value);
}

// Convert a (possibly already-deduped) medications array into the
// comma-separated name string the AI prompts consume. Mirrors the existing
// inline mapping in bcpBuildPatientDataBlock so call sites can swap in this
// helper without changing the prompt's downstream wording.
export function medicationsArrayToPromptString(meds) {
  if (!Array.isArray(meds)) return '';
  return meds
    .map(m => (typeof m === 'string' ? m : (m && m.name) || ''))
    .filter(Boolean)
    .join(', ');
}
